/**
 * The Propagate dialog's model (S8 Task 8.11) — pure. Three modes (D4 typed name for Replace / Remove): Merge (`modify`,
 * a patch of named entries with the EXPLICIT mask of every scope it touches, pre-filled with the root's loaded mask —
 * E5 / row R5), Replace (`set`, a full ACL starting from the root's), Remove (`remove`, named entries only). The root
 * preview is the shared model of what Azure does to the root itself (`recursiveAclResult`), and the
 * copy lines come from `aclRecursiveGuaranteeLines` (never hand-written — finding S8-R2-01); the entry and warning lines
 * of the typed confirmation come from `azureStorageAclChangeText` (shared with the Restart copy and the MCP approval).
 */
import { aclEntryKey as keyOf } from '../../../../core/engines/azureStorageAcl';
import { aclChangeEntryLines, aclChangeWarnings } from '../../../../core/engines/azureStorageAclChangeText';
import { ACL_FAILURES_LINE, aclRecursiveGuaranteeLines, type AclRootIdentity } from '../../../../core/engines/azureStorageAclJobText';
import { computeMask, orderAclEntries, type AclEntryKey } from '../../../../core/engines/azureStorageAclEffective';
import { recursiveAclResult, type AclResultFacts } from '../../../../core/engines/azureStorageAclRecursiveResult';
import type { HnsServiceFacts } from '../../../../core/engines/azureStorageHnsFacts';
import type { StorageAclChange, StorageAclEntry, StorageHnsAccessControl } from '../../../../types/azureStorage';

export type PropagateMode = StorageAclChange['mode'];
export type RemoveEntry = Extract<StorageAclChange, { mode: 'remove' }>['entries'][number];
export type PropagateDraft = { mode: PropagateMode; setEntries: StorageAclEntry[]; modifyEntries: StorageAclEntry[]; removeEntries: RemoveEntry[] };

export const PROPAGATE_MODES: ReadonlyArray<{ mode: PropagateMode; label: string }> = [
  { mode: 'modify', label: 'Merge these entries into every descendant' },
  { mode: 'set', label: "Replace every descendant's ACL with this ACL" },
  { mode: 'remove', label: 'Remove these named entries from every descendant' },
];
export const REPLACE_FOR_MASK_REASON = 'Use Replace to set the mask explicitly';

const clone = (entries: readonly StorageAclEntry[]): StorageAclEntry[] => entries.map((e) => ({ ...e, permissions: { ...e.permissions } }));
const isNamed = (e: { type: string; id?: string }): boolean => (e.type === 'user' || e.type === 'group') && !!e.id;

export function initialPropagateDraft(root: StorageHnsAccessControl, prefill?: StorageAclChange): PropagateDraft {
  const draft: PropagateDraft = { mode: 'modify', setEntries: clone(root.entries), modifyEntries: [], removeEntries: [] };
  if (!prefill) return draft;
  if (prefill.mode === 'set') return { ...draft, mode: 'set', setEntries: clone(prefill.entries) };
  if (prefill.mode === 'modify') return { ...draft, mode: 'modify', modifyEntries: clone(prefill.entries) };
  return { ...draft, mode: 'remove', removeEntries: prefill.entries.map((e) => ({ ...e })) };
}

export function propagateChangeOf(draft: PropagateDraft): StorageAclChange {
  if (draft.mode === 'set') return { mode: 'set', entries: orderAclEntries(draft.setEntries) };
  if (draft.mode === 'modify') return { mode: 'modify', entries: orderAclEntries(draft.modifyEntries) };
  return { mode: 'remove', entries: draft.removeEntries.map((e) => ({ ...e })) };
}

/** The name a Replace / Remove confirmation (start AND Restart, D4 / D14) must be typed with: the root's last segment, or the file-system name. */
export function propagateTypedName(target: { fileSystem: string; path: string }): string {
  return target.path ? target.path.slice(target.path.lastIndexOf('/') + 1) : target.fileSystem;
}

/** Adds a named entry to a Merge patch; a scope without a mask in the patch gets the ROOT's loaded mask (visible, editable). */
export function addPatchEntry(patch: readonly StorageAclEntry[], entry: StorageAclEntry, rootEntries: readonly StorageAclEntry[]): StorageAclEntry[] {
  const next = [...clone(patch), { ...entry, permissions: { ...entry.permissions } }];
  if (!next.some((e) => e.scope === entry.scope && e.type === 'mask')) {
    const rootMask = rootEntries.find((e) => e.scope === entry.scope && e.type === 'mask');
    next.push({ scope: entry.scope, type: 'mask', permissions: rootMask ? { ...rootMask.permissions } : computeMask([...rootEntries, ...next], entry.scope) });
  }
  return orderAclEntries(next);
}

/** Removes a named entry from a Merge patch; the scope's mask goes with its last named entry. */
export function removePatchEntry(patch: readonly StorageAclEntry[], key: AclEntryKey): StorageAclEntry[] {
  const next = patch.filter((e) => keyOf(e) !== keyOf(key));
  return next.filter((e) => e.type !== 'mask' || next.some((x) => x.scope === e.scope && isNamed(x)));
}

/**
 * What the change does to the ROOT's own ACL — the dialog's effective preview ("Other paths may differ"): exactly
 * `recursiveAclResult`, the model the per-path Retry shares (review fixes C #2: default-scope completion, access-only
 * Replace keeping defaults, the service's mask recalculation). The root is a directory, so the model never refuses.
 */
export function rootResultOf(rootEntries: readonly StorageAclEntry[], change: StorageAclChange, facts: AclResultFacts): StorageAclEntry[] {
  const result = recursiveAclResult(change, rootEntries, { isDirectory: true }, facts);
  return 'entries' in result ? result.entries : clone(rootEntries);
}

/** The dialog / approval copy of a recursive change of `root` (first run: with the version read when the dialog opened; a file-system root promises no replacement detection — review fixes C #3). */
export function propagateCopyLines(root: string, shortEtag: string | undefined, facts: HnsServiceFacts, rootIdentity: AclRootIdentity): string[] {
  return [`Applies to ${root} and every file and directory under it.`, ...aclRecursiveGuaranteeLines({ root, shortEtag, facts, rootIdentity }), ACL_FAILURES_LINE];
}

/**
 * The same copy split for reading (UI/UX fix round A, reviews U28 / A17): `summary` is what stays visible — what the
 * change covers, that there is no undo (with the failures it lists) and what happens to paths that change while it runs;
 * `checks` are the remaining `aclRecursiveGuaranteeLines` sentences (verbatim), behind "How Jam SQL checks this change".
 */
export function propagateCopyParts(root: string, shortEtag: string | undefined, facts: HnsServiceFacts, rootIdentity: AclRootIdentity): { summary: string[]; checks: string[] } {
  const guarantees = aclRecursiveGuaranteeLines({ root, shortEtag, facts, rootIdentity });
  const concurrent = guarantees[guarantees.length - 1]; // "Files and directories added, removed or changed under … while the job runs …"
  return { summary: [`Applies to ${root} and every file and directory under it.`, ACL_FAILURES_LINE, concurrent], checks: guarantees.slice(0, -1) };
}

/**
 * The typed confirmation of a Replace / Remove (review opus F2): the applies-to line, every entry the change applies,
 * its mask / default-scope warnings (the form with them is hidden while the name is typed), the no-undo and
 * concurrent-change lines; the checks go under the confirmation's disclosure (U28: never repeated in full up front).
 */
export function propagateConfirmParts(input: { root: string; shortEtag: string | undefined; facts: HnsServiceFacts; rootIdentity: AclRootIdentity; change: StorageAclChange; displayNames: Record<string, string> | undefined }): { lines: string[]; details: string[] } {
  const { summary: [appliesTo, ...rest], checks } = propagateCopyParts(input.root, input.shortEtag, input.facts, input.rootIdentity);
  return { lines: [appliesTo, ...aclChangeEntryLines(input.change, input.displayNames), ...aclChangeWarnings(input.change, input.root, input.facts), ...rest], details: checks };
}

/** Row R5: if Azure did not keep an explicit mask in a recursive merge, a merge with named entries is refused (Replace instead). */
export function modifyMaskProblem(change: StorageAclChange, facts: Pick<HnsServiceFacts, 'recursiveModifyKeepsExplicitMask'>): string | undefined {
  if (change.mode !== 'modify' || facts.recursiveModifyKeepsExplicitMask) return undefined;
  return change.entries.some(isNamed) ? REPLACE_FOR_MASK_REASON : undefined;
}
